import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Check, ChevronLeft, ChevronRight, Download, FileCheck, X } from 'lucide-react'
import { useRole } from '@/app/role'
import { useCurrentSite } from '@/app/sites'
import { useQuestions, useReviewMapping, useSource } from '@/lib/api/hooks'
import type { SourceCitation } from '@/lib/api/types'
import { tok } from '@/charts'
import { Button } from '@/ui/button'
import { RiskChip } from '@/ui/chip'

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1'
const RESP_TONE: Record<string, string> = {
  compliant: tok('--stable'), partially_effective: tok('--ember'), degraded: tok('--clear'),
  unverified: tok('--trace'), temporary_override: tok('--ember'), impaired: tok('--flare'),
  unknown: tok('--faint'), absent: tok('--ignition'),
}
const shortId = (qid: string) => qid.replace(/^BESS-/, '')

type Target = { fileId: string; mappingId?: string }
const Ctx = createContext<{ open: (fileId: string, mappingId?: string) => void }>({ open: () => {} })
export const useSourceViewer = () => useContext(Ctx)

export function SourceViewerProvider({ children }: { children: ReactNode }) {
  const [target, setTarget] = useState<Target | null>(null)
  return (
    <Ctx.Provider value={{ open: (fileId, mappingId) => setTarget({ fileId, mappingId }) }}>
      {children}
      {target && <SourceDrawer target={target} onClose={() => setTarget(null)} />}
    </Ctx.Provider>
  )
}

type Seg = { t: string; lvl: 0 | 1 | 2 }
function segment(text: string, cits: SourceCitation[], focusId?: string): Seg[] {
  const marks = cits
    .filter((c) => c.start != null && c.end != null)
    .map((c) => ({ s: c.start!, e: c.end!, lvl: c.mapping_id === focusId ? 2 : 1 }))
  if (!marks.length) return [{ t: text, lvl: 0 }]
  const bounds = [...new Set([0, text.length, ...marks.flatMap((m) => [m.s, m.e])])].sort((a, b) => a - b)
  const out: Seg[] = []
  for (let i = 0; i < bounds.length - 1; i++) {
    const s = bounds[i], e = bounds[i + 1]
    if (s >= e) continue
    let lvl: 0 | 1 | 2 = 0
    for (const m of marks) if (m.s <= s && m.e >= e) lvl = Math.max(lvl, m.lvl) as 0 | 1 | 2
    out.push({ t: text.slice(s, e), lvl })
  }
  return out
}

function SourceDrawer({ target, onClose }: { target: Target; onClose: () => void }) {
  const { siteId } = useCurrentSite()
  const { role } = useRole()
  const canWrite = role === 'assessor'
  const source = useSource(target.fileId)
  const questions = useQuestions()
  const review = useReviewMapping(siteId ?? '')
  const focusRef = useRef<HTMLElement | null>(null)
  const [actioned, setActioned] = useState<Record<string, 'confirmed' | 'rejected'>>({})

  const qText = useMemo(() => new Map((questions.data ?? []).map((q) => [q.question_id, q.question_text])), [questions.data])
  const located = (source.data?.citations ?? []).filter((c) => c.start != null)
  const initialIdx = Math.max(0, located.findIndex((c) => c.mapping_id === target.mappingId))
  const [idx, setIdx] = useState(0)
  useEffect(() => { setIdx(initialIdx === -1 ? 0 : initialIdx) }, [initialIdx])
  const focus = located[idx]

  // scroll the focused highlight into view once text + focus are ready
  useEffect(() => {
    const t = setTimeout(() => focusRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 60)
    return () => clearTimeout(t)
  }, [source.data, idx])

  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose])

  const segs = source.data ? segment(source.data.text, source.data.citations ?? [], focus?.mapping_id) : []
  let firstStrong = true

  return (
    <div className="fixed inset-0 z-[120] flex justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <aside className="relative flex h-full w-full max-w-[560px] flex-col border-l bg-surface shadow-2xl"
        style={{ animation: 'none' }}>
        {/* header */}
        <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
          <div className="flex min-w-0 items-center gap-2.5">
            <FileCheck className="size-[17px] shrink-0 text-muted-foreground" />
            <div className="min-w-0">
              <div className="truncate text-[13.5px] font-semibold">{source.data?.file_name ?? 'Source document'}</div>
              <div className="text-[11px] text-faint">{located.length} verified citation{located.length === 1 ? '' : 's'}</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <a href={`${API_BASE}/files/${target.fileId}/download`} target="_blank" rel="noreferrer"
              className="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-secondary" title="Download original">
              <Download className="size-[15px]" />
            </a>
            <button onClick={onClose} className="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-secondary" title="Close">
              <X className="size-4" />
            </button>
          </div>
        </div>

        {/* citation bar */}
        {focus && (
          <div className="flex flex-col gap-2.5 border-b bg-surface-2 px-4 py-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 text-[12.5px]">
                Mapped to <b>{shortId(focus.question_id)}</b>
                <span className="text-muted-foreground"> · {qText.get(focus.question_id) ?? ''}</span>
              </div>
              {focus.suggested_response && (
                <RiskChip label={focus.suggested_response} tone={RESP_TONE[focus.suggested_response] ?? tok('--muted-foreground')} />
              )}
            </div>
            <div className="flex items-center justify-between gap-3">
              {actioned[focus.mapping_id] ? (
                <span className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-stable">
                  <Check className="size-3.5" /> {actioned[focus.mapping_id] === 'confirmed' ? 'Accepted — answer applied' : 'Declined'}
                </span>
              ) : canWrite && focus.status === 'pending_review' ? (
                <div className="flex gap-2">
                  <Button size="sm" disabled={review.isPending}
                    onClick={() => review.mutate({ mappingId: focus.mapping_id, action: 'confirm' },
                      { onSuccess: () => setActioned((a) => ({ ...a, [focus.mapping_id]: 'confirmed' })) })}>
                    <Check /> Accept &amp; auto-answer
                  </Button>
                  <Button size="sm" variant="ghost" disabled={review.isPending}
                    onClick={() => review.mutate({ mappingId: focus.mapping_id, action: 'reject' },
                      { onSuccess: () => setActioned((a) => ({ ...a, [focus.mapping_id]: 'rejected' })) })}>
                    <X /> Decline
                  </Button>
                </div>
              ) : (
                <span className="text-[12px] text-faint">
                  {focus.status === 'confirmed' ? 'Confirmed' : !canWrite ? 'Review is assessor-only' : ''}
                </span>
              )}
              <span className="text-[11.5px] text-stable">confidence {Number(focus.confidence ?? 0).toFixed(2)}</span>
            </div>
          </div>
        )}

        {/* reading surface */}
        <div className="flex-1 overflow-auto bg-background px-4 py-5">
          {source.isLoading ? (
            <div className="mx-auto max-w-[60ch] space-y-2">
              {[0, 1, 2, 3].map((i) => <div key={i} className="h-3 rounded bg-surface-2" style={{ width: `${70 + (i % 3) * 10}%` }} />)}
            </div>
          ) : (
            <div className="mx-auto max-w-[62ch] whitespace-pre-wrap rounded-md border bg-surface p-6 text-[13.5px] leading-[1.75] text-muted-foreground">
              {segs.map((s, i) => {
                if (s.lvl === 0) return <span key={i}>{s.t}</span>
                const isFirstStrong = s.lvl === 2 && firstStrong
                if (isFirstStrong) firstStrong = false
                return (
                  <mark key={i}
                    ref={isFirstStrong ? (el) => (focusRef.current = el) : undefined}
                    className="rounded-[3px] px-0.5 text-foreground"
                    style={{ background: s.lvl === 2 ? 'rgb(var(--ember) / 0.34)' : 'rgb(var(--ember) / 0.14)' }}>
                    {s.t}
                  </mark>
                )
              })}
            </div>
          )}
        </div>

        {/* footer: citation nav + provenance */}
        <div className="flex items-center justify-between gap-3 border-t px-4 py-2.5 text-[11px] text-faint">
          {located.length > 1 ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <button onClick={() => setIdx((i) => (i - 1 + located.length) % located.length)} className="hover:text-foreground"><ChevronLeft className="size-4" /></button>
              <span>citation <b className="tabular">{idx + 1}</b> of {located.length}</span>
              <button onClick={() => setIdx((i) => (i + 1) % located.length)} className="hover:text-foreground"><ChevronRight className="size-4" /></button>
            </div>
          ) : <span />}
          <span className="truncate">{focus?.chunk_id ?? ''}{focus?.quote_method ? ` · ${focus.quote_method}` : ''}</span>
        </div>
      </aside>
    </div>
  )
}
